import { useLocation } from 'react-router-dom';
import { 
  Search, 
  Bell, 
  MessageSquare, 
  Sun, 
  Moon, 
  Sparkles,
  Command
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  Tooltip, 
  TooltipContent, 
  TooltipTrigger 
} from '@/components/ui/tooltip';
import { useGlobalStore } from '@/core/store';
import { cn } from '@/lib/utils';
import { useEffect } from 'react';

// 路由标题映射
const routeTitles: Record<string, string> = {
  '/dashboard': '仪表盘',
  '/accounts': '账号管理',
  '/content': '内容管理',
  '/tracks': '赛道管理',
  '/competitors': '竞品分析',
  '/clusters': '集群管理',
  '/analytics': '数据分析',
  '/schedule': '排期日历',
  '/team': '团队协作',
  '/ai-studio': 'AI 工作室',
  '/settings': '系统设置',
};

export function Header() {
  const location = useLocation();
  const { theme, setTheme, toggleAISidebar, user } = useGlobalStore();

  // 获取当前页面标题
  const currentTitle = Object.entries(routeTitles).find(
    ([path]) => location.pathname === path || location.pathname.startsWith(path + '/')
  )?.[1] || '页面';

  // 快捷键监听
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd/Ctrl + K 打开 AI 侧边栏
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        toggleAISidebar();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleAISidebar]);

  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  };

  return (
    <header className="h-16 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="flex items-center justify-between h-full px-6">
        {/* 左侧：页面标题 */}
        <div className="flex items-center gap-4">
          <h2 className="text-lg font-semibold">{currentTitle}</h2>
        </div>

        {/* 中间：搜索框 */}
        <div className="flex-1 max-w-md mx-8">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="搜索账号、内容、数据..."
              className="pl-10 pr-12 bg-secondary/50 border-none focus-visible:ring-hermes-500"
            />
            <kbd className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none hidden sm:inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
              <Command className="h-3 w-3" />K
            </kbd>
          </div>
        </div>

        {/* 右侧：工具栏 */}
        <div className="flex items-center gap-2">
          {/* AI 助手按钮 */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="hermes-ghost"
                size="icon"
                onClick={toggleAISidebar}
                className="relative"
              >
                <Sparkles className="h-5 w-5" />
                <span className="absolute -top-1 -right-1 w-2 h-2 bg-hermes-500 rounded-full animate-pulse" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>AI 助手 <kbd className="ml-1 text-xs">⌘K</kbd></p>
            </TooltipContent>
          </Tooltip>

          {/* 消息 */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" className="relative">
                <MessageSquare className="h-5 w-5" />
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-hermes-500 text-white text-xs rounded-full flex items-center justify-center">
                  3
                </span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>消息</p>
            </TooltipContent>
          </Tooltip>

          {/* 通知 */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" className="relative">
                <Bell className="h-5 w-5" />
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  5
                </span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>通知</p>
            </TooltipContent>
          </Tooltip>

          {/* 主题切换 */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" onClick={toggleTheme}>
                {theme === 'dark' ? (
                  <Sun className="h-5 w-5" />
                ) : (
                  <Moon className="h-5 w-5" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>切换主题</p>
            </TooltipContent>
          </Tooltip>

          {/* 用户头像 */}
          <div className="ml-2 flex items-center gap-3 pl-4 border-l border-border">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium">{user?.username || '未登录'}</p>
              <p className="text-xs text-muted-foreground">{user?.role || '访客'}</p>
            </div>
            <Avatar className="h-9 w-9 border-2 border-hermes-500/20">
              <AvatarImage src={user?.avatar} />
              <AvatarFallback className="bg-hermes-500 text-white">
                {user?.username?.charAt(0)?.toUpperCase() || 'U'}
              </AvatarFallback>
            </Avatar>
          </div>
        </div>
      </div>
    </header>
  );
}

